import { motion } from 'motion/react';
import { Check, Sparkles, Zap, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const teaserPlans = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Perfect for trying AI email drafts',
    highlights: [
      '30 generations/month',
      'Gmail integration',
      'GPT-4o-mini',
    ],
    highlighted: false,
    badge: null,
  },
  {
    name: 'Pro',
    price: '$30',
    period: '/mo',
    description: 'Advanced AI with style mimicking',
    highlights: [
      '500 generations/month',
      'Gmail & LinkedIn',
      'GPT-4o, Grok & Claude',
      'Style mimicking',
    ],
    highlighted: true,
    badge: 'Most Popular',
  },
  {
    name: 'Ultimate',
    price: '$100',
    period: '/mo',
    description: 'Maximum scale + all packages',
    highlights: [
      '10,000 generations/month',
      'All role packages included',
      'VIP priority support',
    ],
    highlighted: false,
    badge: 'Ultimate',
  },
];

export function Pricing() {
  return (
    <section id="pricing" className="py-20 bg-gray-50/80 dark:bg-[#0F2847]/60 backdrop-blur-sm">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Start free. Upgrade anytime for more generations and superior AI quality.
          </p>
        </motion.div>

        {/* 3 Mini-Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {teaserPlans.map((plan, index) => (
            <motion.div
              key={plan.name}
              className={`relative p-6 rounded-2xl border-2 transition-all ${
                plan.name === 'Ultimate'
                  ? 'bg-gradient-to-br from-amber-500 via-yellow-500 to-amber-600 border-amber-400 shadow-xl shadow-amber-500/20'
                  : plan.highlighted
                  ? 'bg-gradient-to-br from-[#1E3A8A] to-[#60A5FA] border-[#60A5FA] shadow-xl shadow-[#60A5FA]/20'
                  : 'bg-white dark:bg-[#1E3A8A]/10 border-gray-200 dark:border-[#1E3A8A]/30 shadow-md'
              }`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -5 }}
            >
              {plan.badge === 'Most Popular' && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#60A5FA] text-white rounded-full flex items-center gap-1">
                  <Sparkles size={14} />
                  <span className="text-sm font-semibold">Most Popular</span>
                </div>
              )}
              {plan.badge === 'Ultimate' && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-amber-600 to-yellow-600 text-white rounded-full flex items-center gap-1 shadow-lg">
                  <Sparkles size={14} className="animate-pulse" />
                  <span className="text-sm font-semibold">Ultimate</span>
                </div>
              )}

              {/* Price */}
              <div className="mb-5 mt-2">
                <h3 className={`text-xl font-bold mb-1 ${plan.highlighted || plan.name === 'Ultimate' ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                  {plan.name}
                </h3>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className={`text-4xl font-bold ${plan.highlighted || plan.name === 'Ultimate' ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                    {plan.price}
                  </span>
                  <span className={`text-sm ${plan.highlighted || plan.name === 'Ultimate' ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                    {plan.period}
                  </span>
                </div>
                <p className={`text-sm ${plan.highlighted || plan.name === 'Ultimate' ? 'text-white/90' : 'text-gray-600 dark:text-gray-400'}`}>
                  {plan.description}
                </p>
              </div>

              {/* Highlights */}
              <ul className="space-y-3">
                {plan.highlights.map((text) => (
                  <li key={text} className="flex items-center gap-2">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                      plan.highlighted || plan.name === 'Ultimate' ? 'bg-white/25' : 'bg-[#60A5FA]/20'
                    }`}>
                      <Check size={13} className={plan.highlighted || plan.name === 'Ultimate' ? 'text-white' : 'text-[#60A5FA]'} />
                    </div>
                    <span className={`text-sm ${plan.highlighted || plan.name === 'Ultimate' ? 'text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                      {text}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Annual savings note */}
        <motion.p
          className="text-center text-sm text-gray-500 dark:text-gray-400 mb-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          Annual billing available — save up to 30%. Plus 2 more plans (Starter & Pro+).
        </motion.p>

        {/* CTAs */}
        <motion.div
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#1E3A8A] to-[#60A5FA] text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-[#60A5FA]/30 transition-all hover:scale-[1.02]"
          >
            <Zap size={20} />
            Get Started Free
          </Link>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white dark:bg-white/10 text-[#1E3A8A] dark:text-white font-semibold rounded-lg border border-gray-200 dark:border-white/20 hover:bg-gray-50 dark:hover:bg-white/15 transition-all"
          >
            Compare All Plans
            <ArrowRight size={18} />
          </Link>
        </motion.div>

        {/* Enterprise CTA Banner */}
        <motion.div
          className="max-w-5xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <div className="relative p-8 rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 dark:from-gray-950 dark:to-gray-900 border-2 border-gray-700 shadow-2xl overflow-hidden">
            <div className="absolute inset-0 opacity-5">
              <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
            </div>
            <div className="relative z-10 text-center">
              <h3 className="text-3xl font-bold text-white mb-3">
                Looking for Enterprise Solutions?
              </h3>
              <p className="text-lg text-gray-300 mb-4 max-w-3xl mx-auto">
                Custom pricing for teams and organizations that need dedicated support, SSO, API access, on-premise deployment, and compliance guarantees.
              </p>
              <p className="text-sm text-gray-400 mb-6">
                Starting ~$500+/mo · 50,000+ generations/org/month · Flexible Pay-As-You-Go rates
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 mb-6 text-sm text-gray-400">
                <span className="flex items-center gap-1">
                  <Check size={16} className="text-[#60A5FA]" />
                  24/7 Dedicated Support
                </span>
                <span className="flex items-center gap-1">
                  <Check size={16} className="text-[#60A5FA]" />
                  Team Management & SSO
                </span>
                <span className="flex items-center gap-1">
                  <Check size={16} className="text-[#60A5FA]" />
                  Full API Access
                </span>
                <span className="flex items-center gap-1">
                  <Check size={16} className="text-[#60A5FA]" />
                  On-Premise Deployment
                </span>
                <span className="flex items-center gap-1">
                  <Check size={16} className="text-[#60A5FA]" />
                  Custom Model Training
                </span>
              </div>
              <a
                href="/enterprise"
                className="inline-block px-8 py-4 bg-[#60A5FA] text-white font-semibold rounded-lg hover:bg-[#60A5FA]/90 transition-all shadow-lg shadow-[#60A5FA]/30 hover:shadow-xl hover:shadow-[#60A5FA]/40"
              >
                Explore Enterprise Options →
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
